import { IconButton } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Card from "../../../components/ui/Card";
import { useScriptMode } from "../../../contexts/ScriptModeContext";

const TR_MONTHS = [
  "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
  "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
];

function StatTile({ value, label, loading, index = 0 }) {
  return (
    <div
      className="px-4 py-2.5 rounded-xl bg-terakkiBrandCloud min-w-[104px] motion-safe:animate-pop-in"
      style={{ animationDelay: `${100 + index * 60}ms` }}
    >
      {loading ? (
        <div className="h-6 w-12 rounded bg-terakkiBrandMist animate-pulse" />
      ) : (
        <div className="text-xl font-SemiBold text-terakkiPrimaryDark leading-tight">{value}</div>
      )}
      <div className="text-sm text-terakkiNeutral leading-tight mt-0.5">{label}</div>
    </div>
  );
}

export default function MonthSummaryHeader({ monthDate, summary, totalDays, loading, onPrev, onNext, canGoNext = true }) {
  const { t } = useScriptMode();
  const logged = summary?.loggedCount ?? 0;
  // Ay ilerlemesi: kayıt girilen günlerin oranı. Ortalama yüzdeden farklı bir şey
  // söylüyor — "ne kadar düzenli tuttum" sorusunun cevabı.
  const filledRatio = totalDays ? Math.round((logged / totalDays) * 100) : 0;

  return (
    <Card data-tour="month" className="flex flex-col gap-4 motion-safe:animate-rise-in">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex items-center gap-2.5">
            <span className="w-10 h-10 rounded-xl bg-terakkiBrandCloud text-terakkiPrimaryDark flex items-center justify-center shrink-0">
              <CalendarMonthIcon aria-hidden />
            </span>
            <h1 className="text-2xl font-SemiBold text-terakkiPrimaryDark leading-none">{t("nav_profile")}</h1>
          </span>
          <span className="hidden sm:block w-px h-8 bg-terakkiBorder" aria-hidden />
          <div className="flex items-center gap-1">
            <IconButton size="small" onClick={onPrev} aria-label={t("calendar_prev_month")}>
              <ChevronLeftIcon />
            </IconButton>
            <div className="text-center min-w-[150px]">
              <div className="text-sm uppercase tracking-wide text-terakkiNeutral font-SemiBold">
                {t("calendar_month_summary")}
              </div>
              <div className="text-lg font-SemiBold text-terakkiInk leading-tight">
                {TR_MONTHS[monthDate.getMonth()]} {monthDate.getFullYear()}
              </div>
            </div>
            <IconButton
              size="small"
              onClick={onNext}
              disabled={!canGoNext}
              aria-label={t("calendar_next_month")}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <StatTile loading={loading} value={`★ ${summary?.stars ?? 0}`} label={t("calendar_summary_stars")} index={0} />
          <StatTile loading={loading} value={summary?.longestChain ?? 0} label={t("calendar_summary_chain")} index={1} />
          <StatTile loading={loading} value={`%${summary?.average ?? 0}`} label={t("calendar_summary_average")} index={2} />
          <StatTile loading={loading} value={`${logged}/${totalDays}`} label={t("calendar_summary_logged")} index={3} />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between text-sm text-terakkiNeutral mb-1">
          <span>{t("calendar_month_fill")}</span>
          <span>%{loading ? 0 : filledRatio}</span>
        </div>
        <div className="h-1.5 rounded-full bg-terakkiBrandMist overflow-hidden">
          <div
            data-testid="month-fill-bar"
            className="h-full rounded-full bg-terakkiPrimaryDark dark:!bg-white transition-[width] duration-300"
            style={{ width: `${loading ? 0 : filledRatio}%` }}
          />
        </div>
      </div>
    </Card>
  );
}
